import {
    Button,
    Col,
    Row,
    Spin,
    Typography,
    Tabs,
    Card,
    Badge,
    Space,
    Tooltip,
} from "antd";
import { useNavigate, useParams } from "react-router";
import {
    useCallback,
    useEffect,
    useState,
    CSSProperties,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import {
    getInstitutions,
    IInstitution,
    SET_TEST_INSTITUTION,
} from "../../redux/reducer/institution";
import ThemeConfiguration from "../organisms/ThemeConfiguration";
import { useMsal } from "@azure/msal-react";
import {
    ArrowLeftOutlined,
    SettingOutlined,
    ApiOutlined,
    OrderedListOutlined,
    BgColorsOutlined,
    MailOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import StepsConfiguration from "../organisms/StepsConfiguration";
import AdminStep from "../AdminStep";
import { ConfigStepId, ConfigStepType } from "../../utils/configuration";
import PreviewModal from "../molecules/PreviewModal";
import SettingsConfiguration from "../organisms/SettingsConfiguration";
import IntegrationConfiguration from "./IntegrationConfiguration";
import EmailConfiguration from "../organisms/EmailConfiguration";

const { Title } = Typography;
const { TabPane } = Tabs;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '24px',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
    },
    header: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },
    backButton: {
        fontSize: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    mainContent: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },
    stepCard: {
        marginBottom: '16px',
        borderRadius: '8px',
        transition: 'all 0.3s',
        cursor: 'pointer',
    },
    stepCardHover: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    tabIcon: {
        fontSize: '16px',
        marginRight: '8px',
    },
};

interface InstitutionConfigurationParams {
}

const InstitutionConfiguration = ({
}: InstitutionConfigurationParams) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const { institutionId } = useParams();
    const navigate = useNavigate();

    const [previewId, setPreviewId] = useState<ConfigStepId | null>(null);

    const institution = useSelector<RootState, IInstitution | undefined>(({ institution }) => institution.testInstitution);
    const loading = useSelector<RootState, boolean>(({ status }) => status.loading['institution/GET_INSTITUTIONS']);

    const fetchInstitutions = useCallback(() => {
        dispatch(getInstitutions({ headers: { Authorization: `Bearer ${accounts[0].idToken}` } }))
    }, []);

    useEffect(() => { fetchInstitutions(); }, []);

    useEffect(() => {
        if (institutionId) dispatch({ type: SET_TEST_INSTITUTION, id: institutionId });
        return () => {
            dispatch({ type: SET_TEST_INSTITUTION, id: undefined });
        }
    }, [institutionId, loading]);

    const [activeTab, setActiveTab] = useState<string>('settings');
    const [hoveredStep, setHoveredStep] = useState<string | null>(null);

    const renderStepCard = (step: ConfigStepType, stepIndex: number) => {
        const isHovered = hoveredStep === step.id;

        return (
            <Card
                key={step.id}
                style={{
                    ...styles.stepCard,
                    ...(isHovered ? styles.stepCardHover : {}),
                }}
                onMouseEnter={() => setHoveredStep(step.id)}
                onMouseLeave={() => setHoveredStep(null)}
            >
                <Row justify="space-between" align="middle">
                    <Col flex="1">
                        <Space>
                            <Title level={5} style={{ margin: 0 }}>{step.name}</Title>
                            {step.enabled && (
                                <Badge status="success" text="Enabled" />
                            )}
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            ghost
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewId(step.id);
                            }}
                        >
                            Preview
                        </Button>
                    </Col>
                </Row>
                <div style={{ marginTop: '16px' }}>
                    <AdminStep step={step} stepIndex={stepIndex} />
                </div>
            </Card>
        );
    };

    return loading ? <Spin fullscreen /> : (
        <div style={styles.container}>
            <PreviewModal id={previewId} onClose={() => { setPreviewId(null); }} />

            {/* Header */}
            <div style={styles.header}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space size="large">
                            <Tooltip title="Back to Admin">
                                <ArrowLeftOutlined
                                    style={styles.backButton}
                                    onClick={() => navigate('/admin')}
                                />
                            </Tooltip>
                            <Title level={2} style={{ margin: 0 }}>
                                {institution?.name}
                            </Title>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Main Content */}
            <div style={styles.mainContent}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    size="large"
                    type="card"
                >
                    <TabPane
                        tab={
                            <span>
                                <SettingOutlined style={styles.tabIcon} />
                                Settings
                            </span>
                        }
                        key="settings"
                    >
                        {institution && <SettingsConfiguration institution={institution} />}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <ApiOutlined style={styles.tabIcon} />
                                Integration
                            </span>
                        }
                        key="integration"
                    >
                        {institution && <IntegrationConfiguration institution={institution} />}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <OrderedListOutlined style={styles.tabIcon} />
                                Steps Configuration
                            </span>
                        }
                        key="steps-config"
                    >
                        {institution && <StepsConfiguration institution={institution} />}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <BgColorsOutlined style={styles.tabIcon} />
                                Theme
                            </span>
                        }
                        key="theme"
                    >
                        {institution && <ThemeConfiguration institution={institution} />}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <MailOutlined style={styles.tabIcon} />
                                Email
                            </span>
                        }
                        key="email"
                    >
                        {institution && <EmailConfiguration institution={institution} />}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <OrderedListOutlined style={styles.tabIcon} />
                                Workflow Steps
                            </span>
                        }
                        key="workflow-steps"
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Title level={4}>Configure Individual Steps</Title>
                            {institution?.services
                                .map((step: ConfigStepType, stepIndex: number) => ({ component: renderStepCard(step, stepIndex), enabled: step.enabled || step.conditionalEnabling }))
                                .filter(({ enabled }) => enabled)
                                .map(({ component }) => component)
                            }
                        </Space>
                    </TabPane>
                </Tabs>
            </div>
        </div>
    );
};

export default InstitutionConfiguration;
